import { useEffect, useState, useRef } from 'react'
import { useLocalStorage } from '../../../hooks/useLocalStorage'
import { Dialog } from 'primereact/dialog'
import { Button } from 'primereact/button'
import * as Sentry from '@sentry/react'
import client from '../../../client'
import { Trans, useTranslation } from 'react-i18next'
import { showErrorNotification } from '../../../redux/actions/notificationActions'
import { useDispatch, useSelector } from 'react-redux'
import jwt_decode from 'jwt-decode'
import Language from './Language'
import { GameState } from '../../../redux/types/game'
import { getGameSelector } from '../../../redux/selectors/gameSelectors'
import * as DOMPurify from 'dompurify'
import { fetchGameConsentRequest } from '../../../redux/actions/gameActions'
import { decodeIframeString } from '../../../utils/decodeIframeString'

// import i18n from '../../../i18n'

const Consent = () => {
  const {
    data: { branding, consentHtml, selectedLanguage },
    loading: { isLoadingConsentHtml },
  }: GameState = useSelector(getGameSelector)
  const token = localStorage.getItem('tokenCityHunt') ?? ''
  const { TeamId, UserName, HasConsent, GameId }: any = jwt_decode(token)

  const [storedValue, setValue] = useLocalStorage(
    `foxHuntConsentAllowedWithTeamId-${TeamId}`,
    false,
  )
  const [isLoadingConsent, setIsLoadingConsent] = useState(false)
  const [showConsent, setShowConsent] = useState(false)
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const [isAgreeDisabled, setIsAgreeDisabled] = useState(true)
  const consentDivRef = useRef<any>(null)

  const handleScroll = () => {
    if (consentDivRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = consentDivRef.current
      if (scrollTop + clientHeight >= scrollHeight - 30) {
        setIsAgreeDisabled(false)
      }
    }
  }

  useEffect(() => {
    if (selectedLanguage) {
      dispatch(
        fetchGameConsentRequest({
          gameId: GameId,
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          localeId: selectedLanguage.id,
        }),
      )
    }
  }, [selectedLanguage])

  useEffect(() => {
    const consentDiv = consentDivRef.current
    if (consentDiv) {
      consentDiv.addEventListener('scroll', handleScroll)
      return () => {
        consentDiv.removeEventListener('scroll', handleScroll)
      }
    }
  }, [consentDivRef.current])

  // console.log(Object.keys(i18n.services.resourceStore.data))
  const handleAllowConsent = async () => {
    setIsLoadingConsent(true)
    const metaData: any = {
      referer: document.referrer,
      timezoneOffset: new Date().getTimezoneOffset(),
      userLanguage: navigator.language,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      userAgent: navigator.userAgent,
    }
    await client
      .post('Team/PostConsent', null, {
        params: {
          isAllowed: true,
          metaData: JSON.stringify(metaData),
        },
        headers: {
          Authorization: `Bearer ${JSON.parse(token)}`,
        },
      })
      .then((response) => {
        if (response) {
          setIsLoadingConsent(false)
          setValue(true)
        }
      })
      .catch((err) => {
        Sentry.captureException(err)
        dispatch(
          showErrorNotification('Something went wrong, please try again!'),
        )
        setIsLoadingConsent(false)
      })
  }

  useEffect(() => {
    if (HasConsent === 'True') {
      setShowConsent(true)
    }
  }, [HasConsent])

  const sanitizedData = (data: string) => ({
    __html: DOMPurify.sanitize(data, {
      ADD_TAGS: ['iframe'],
      ADD_ATTR: ['src', 'class', 'allowfullscreen', 'frameborder'],
    }),
  })

  const onLanguageChange = (id: number) => {
    dispatch(
      fetchGameConsentRequest({
        gameId: GameId,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        localeId: id,
      }),
    )
  }

  Sentry.setContext('Variables', {
    GameId,
    HasConsent,
    TeamId,
    storedValue,
    showConsent,
    isAgreeDisabled,
  })

  if (storedValue || !showConsent) {
    return null
  }
  return (
    <Dialog
      header={() => (
        <div className="flex flex-column align-items-center">
          {/* <h1 style={{ marginBottom: 0 }}>{t('Consent')}</h1> */}
          <Language onChange={onLanguageChange} />
        </div>
      )}
      visible={!storedValue}
      style={{ width: '94vw', height: '90%' }}
      closable={false}
      onHide={() => null}
      headerStyle={{
        backgroundColor: branding?.modals.backgroundColor,
        color: branding?.modals.textColor,
      }}
      footer={() => (
        <Button
          label={t('Agree')}
          icon={
            <>
              <div
                className="p-2 p-button-icon p-button-icon-right rounded-icon-style"
                style={{
                  backgroundColor: branding?.buttonPrimary.textColor,
                }}
              ></div>
              <i
                className="pi pi-check icon-center"
                style={{ color: branding?.buttonPrimary.backgroundColor }}
              ></i>
            </>
          }
          loading={isLoadingConsent}
          onClick={handleAllowConsent}
          className="p-button-rounded w-full"
          style={{
            backgroundColor: branding?.buttonPrimary.backgroundColor,
            color: branding?.buttonPrimary.textColor,
          }}
          disabled={isAgreeDisabled}
        />
      )}
    >
      {!isLoadingConsentHtml ? (
        <div
          ref={consentDivRef}
          style={{ overflowWrap: 'break-word' }}
          className="img-full-width ql-editor my-3"
          dangerouslySetInnerHTML={sanitizedData(
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            decodeIframeString(consentHtml?.html ?? '') ?? '',
          )}
        />
      ) : (
        <h3>Loading Consent...</h3>
      )}
    </Dialog>
  )
}

export default Consent
